/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { accessAPI, Titulo, Boton, Formulario, Sidebar, TablaFiltrable } from "../../../shared";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Menu } from "primereact/menu";
import { Tooltip } from "primereact/tooltip";
import { confirmDialog } from "primereact/confirmdialog";

/**
 * Card de descuentos de un cliente. Lista los descuentos por tipo de
 * certificado y permite agregar, editar y eliminar. Los endpoints viven
 * en adminCliente.js: GET /admin/cliente/:id/descuentos, POST /admin/cliente/:id/descuento,
 * PUT /admin/cliente/descuento/:id, DELETE /admin/cliente/descuento/:id.
 */
export default function CardDescuentos({ clienteId }) {
  const [descuentos, setDescuentos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarAgregar, setSidebarAgregar] = useState(false);
  const [sidebarEditar, setSidebarEditar] = useState(false);
  const [loaderAgregar, setLoaderAgregar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [descuentoSeleccionado, setDescuentoSeleccionado] = useState(null);
  const [descuentoEnProceso, setDescuentoEnProceso] = useState(null);

  const toast = useRef(null);
  const menuRef = useRef(null);

  function cargarDescuentos() {
    setLoading(true);
    accessAPI(
      "GET",
      `admin/cliente/${clienteId}/descuentos`,
      null,
      (respuesta) => {
        setDescuentos(respuesta);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }

  useEffect(() => {
    if (clienteId) cargarDescuentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  function agregarDescuentoSubmit(formData) {
    const ids = Array.isArray(formData.certificadoTipoIds)
      ? formData.certificadoTipoIds
      : [];
    if (ids.length === 0) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Seleccioná al menos un tipo de certificado",
        sticky: true
      });
      return;
    }

    setLoaderAgregar(true);
    const creados = [];
    const errores = [];
    let pendientes = ids.length;

    const finalizar = () => {
      setLoaderAgregar(false);
      if (creados.length > 0) {
        setDescuentos((prev) => [...(prev || []), ...creados]);
      }
      if (errores.length === 0) {
        setSidebarAgregar(false);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail:
            creados.length === 1
              ? "Descuento creado"
              : `${creados.length} descuentos creados`
        });
      } else if (creados.length === 0) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: errores[0] || "Error al crear descuentos",
          sticky: true
        });
      } else {
        setSidebarAgregar(false);
        toast.current?.show({
          severity: "warn",
          summary: "Parcial",
          detail: `Se crearon ${creados.length} de ${ids.length} descuentos. ${errores[0] || ""}`,
          sticky: true
        });
      }
    };

    ids.forEach((certificadoTipoId) => {
      accessAPI(
        "POST",
        `admin/cliente/${clienteId}/descuento`,
        {
          certificadoTipoId,
          porcentajeDescuento: formData.porcentajeDescuento,
          concepto: formData.concepto || ""
        },
        (respuesta) => {
          const nuevo = respuesta[0]?.descuento;
          if (nuevo) creados.push(nuevo);
          pendientes -= 1;
          if (pendientes === 0) finalizar();
        },
        (respuesta) => {
          errores.push(respuesta[0]?.msg || "Error al crear descuento");
          pendientes -= 1;
          if (pendientes === 0) finalizar();
        }
      );
    });
  }

  function editarDescuentoSubmit(formData) {
    if (!descuentoSeleccionado) return;
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/cliente/descuento/${descuentoSeleccionado.id}`,
      {
        porcentajeDescuento: formData.porcentajeDescuento,
        concepto: formData.concepto || ""
      },
      (respuesta) => {
        setLoaderEditar(false);
        setSidebarEditar(false);
        const actualizado = respuesta[0]?.descuento;
        if (actualizado) {
          setDescuentos((prev) =>
            prev.map((d) => (d.id === actualizado.id ? actualizado : d))
          );
        }
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta[0]?.msg || "Descuento actualizado"
        });
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar descuento",
          sticky: true
        });
      }
    );
  }

  function eliminarDescuento(descuento) {
    setDescuentoEnProceso(descuento.id);
    accessAPI(
      "DELETE",
      `admin/cliente/descuento/${descuento.id}`,
      null,
      (respuesta) => {
        setDescuentoEnProceso(null);
        setDescuentos((prev) => prev.filter((d) => d.id !== descuento.id));
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta[0]?.msg || "Descuento eliminado"
        });
      },
      (respuesta) => {
        setDescuentoEnProceso(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar descuento",
          sticky: true
        });
      }
    );
  }

  const agregarFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "certificadoTipoIds",
            tipoitemformularioid: 12,
            etiqueta: "Tipos de certificado",
            requerido: true,
            opciones: "admin/config/certificadotipo"
          },
          {
            nombre: "porcentajeDescuento",
            tipoitemformularioid: 8,
            etiqueta: "Porcentaje de descuento (%)",
            requerido: true
          },
          {
            nombre: "concepto",
            tipoitemformularioid: 11,
            etiqueta: "Concepto",
            requerido: true
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Agregar descuento",
        callback: agregarDescuentoSubmit,
        validar: true
      }
    ]
  };

  const editarFormConfig = descuentoSeleccionado
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "certificadoTipo",
                tipoitemformularioid: 1,
                etiqueta: "Tipo de certificado",
                respuesta: `${descuentoSeleccionado.certificadoTipo?.procesotipo?.nombre || ""} - ${descuentoSeleccionado.certificadoTipo?.nombre || ""}`,
                disabled: true
              },
              {
                nombre: "porcentajeDescuento",
                tipoitemformularioid: 8,
                etiqueta: "Porcentaje de descuento (%)",
                requerido: true,
                respuesta: descuentoSeleccionado.porcentajeDescuento
              },
              {
                nombre: "concepto",
                tipoitemformularioid: 11,
                etiqueta: "Concepto",
                requerido: true,
                respuesta: descuentoSeleccionado.concepto
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar cambios",
            callback: editarDescuentoSubmit,
            validar: true
          }
        ]
      }
    : null;

  const menuItems = [
    {
      label: "Editar descuento",
      icon: "pi pi-pencil",
      command: () => {
        if (descuentoSeleccionado) setSidebarEditar(true);
      }
    },
    {
      label: "Eliminar descuento",
      icon: "pi pi-trash",
      command: () => {
        if (!descuentoSeleccionado) return;
        const aEliminar = descuentoSeleccionado;
        confirmDialog({
          message: `¿Eliminar el descuento de ${aEliminar.porcentajeDescuento}% para ${aEliminar.certificadoTipo?.procesotipo?.nombre || ""} - ${aEliminar.certificadoTipo?.nombre || ""}?`,
          header: "Confirmar eliminación",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Eliminar",
          rejectLabel: "Cancelar",
          acceptClassName: "p-button-danger",
          accept: () => eliminarDescuento(aEliminar)
        });
      }
    }
  ];

  const certificadoTipoTemplate = (row) =>
    `${row.certificadoTipo?.procesotipo?.nombre || ""} - ${row.certificadoTipo?.nombre || ""}`;

  const conceptoTemplate = (row) => {
    if (!row.concepto) return null;
    return (
      <>
        <Tooltip target={`.concepto-${row.id}`} />
        <i
          className={`pi pi-info-circle concepto-${row.id}`}
          data-pr-tooltip={row.concepto}
          data-pr-position="top"
          css={{
            color: "var(--amarillo)",
            cursor: "pointer"
          }}
        />
      </>
    );
  };

  const porcentajeTemplate = (row) => `${row.porcentajeDescuento}%`;

  const accionesTemplate = (row) => (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.5em"
      }}
    >
      {descuentoEnProceso === row.id && (
        <i className="pi pi-spin pi-spinner" />
      )}
      <Boton
        texto
        negro
        chico
        callback={(e) => {
          e.stopPropagation();
          setDescuentoSeleccionado(row);
          menuRef.current.toggle(e);
        }}
      >
        <i className="pi pi-ellipsis-v" />
      </Boton>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Menu model={menuItems} popup ref={menuRef} />

      {/* Sidebar agregar descuento */}
      <Sidebar
        visible={sidebarAgregar}
        position="right"
        onHide={() => setSidebarAgregar(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nuevo descuento</Titulo>
        <Formulario config={agregarFormConfig} loader={loaderAgregar} />
      </Sidebar>

      {/* Sidebar editar descuento */}
      <Sidebar
        visible={sidebarEditar}
        position="right"
        onHide={() => setSidebarEditar(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar descuento</Titulo>
        {editarFormConfig && (
          <Formulario config={editarFormConfig} loader={loaderEditar} />
        )}
      </Sidebar>

      <Card>
        <Titulo
          botones={
            <Boton
              texto
              negro
              chico
              callback={() => setSidebarAgregar(true)}
            >
              <i className="pi pi-plus" />
            </Boton>
          }
        >
          Descuentos
        </Titulo>
        {loading ? (
          <p css={{ color: "var(--grisTextos)" }}>Cargando...</p>
        ) : (
          <TablaFiltrable conFiltros={false}
            value={descuentos || []}
            size="small"
            emptyMessage="No hay descuentos configurados"
            dataKey="id"
            nombreArchivo="descuentos_cliente"
          >
            <Column header="Tipo de certificado" body={certificadoTipoTemplate} />
            <Column
              header=""
              body={conceptoTemplate}
              headerStyle={{ width: "3em" }}
              bodyStyle={{ textAlign: "center" }}
            />
            <Column
              header="Descuento"
              body={porcentajeTemplate}
              headerStyle={{ width: "8em", textAlign: "center" }}
              bodyStyle={{ textAlign: "center" }}
            />
            <Column
              body={accionesTemplate}
              style={{ width: "80px", textAlign: "center" }}
            />
          </TablaFiltrable>
        )}
      </Card>
    </>
  );
}
